import type { CommitmentItem, CommitmentLine } from '../budget/cashflow';
import { addDays, type LocalDate } from '../dates';
import { inRange, type DateRange } from '../ledger/counting';
import type { Mutation } from '../mutations';
import type { BuildCtx } from '../spending/types';
import { dueInstallmentCharges, installmentCharges, type InstallmentCharge } from './installments';
import { allDues, expenseDateFor, isOpen, payeeDues, recordedCharges } from './project';
import {
  cardMethod,
  installmentEntryId,
  paymentsMutation,
  type Due,
  type InstallmentLike,
  type PayeeLike,
  type PaymentsSnapshot,
} from './types';

export type PaymentLineKey =
  'bills' | 'utilities' | 'loans' | 'installments' | 'cards' | 'card_statements';

export const LINE_LABELS: Record<PaymentLineKey, string> = {
  bills: 'Fixed bills',
  utilities: 'Utilities',
  loans: 'Loans',
  installments: 'Card installments',
  cards: 'Card balances from before Tahi',
  card_statements: 'Card statements',
};

const lineOf = (kind: PayeeLike['kind']): PaymentLineKey | null =>
  kind === 'bill' ? 'bills' : kind === 'utility' ? 'utilities' : kind === 'loan' ? 'loans' : null;

/**
 * The unpaid rest of a due on its due date; once that date has passed, on today instead, marked
 * overdue, so it's held back in the current period until paid or closed (and not in its own past
 * period, so it never counts twice).
 */
function owedItem(d: Due, amountMinor: number, label: string, today: LocalDate): CommitmentItem {
  const overdue = d.dueDate < today;
  return {
    id: d.id,
    label,
    date: overdue ? today : d.dueDate,
    amountMinor,
    estimated: d.estimated,
    paid: false,
    ledgerEntryId: null,
    ...(overdue ? { overdue: true } : {}),
  };
}

/**
 * One due as cash-flow items: each payment made (dated like its expense, pointing at its ledger
 * row so "Other spending" never counts it again) plus the unpaid rest while it's open. Skipped
 * and closed rests are not owed; dues from before Tahi aren't Tahi money.
 */
function dueItems(d: Due, today: LocalDate): CommitmentItem[] {
  if (d.status === 'prepaid') return [];
  const items: CommitmentItem[] = d.payments.map((p) => ({
    id: p.id,
    label: d.payeeName,
    date: expenseDateFor(p.paidDate, d.dueDate),
    amountMinor: p.amountMinor,
    estimated: false,
    paid: true,
    ledgerEntryId: p.ledgerEntryId,
  }));
  if (isOpen(d) && d.remainingMinor > 0)
    items.push(owedItem(d, d.remainingMinor, d.payeeName, today));
  return items;
}

/**
 * A card's opening balance (the statement built from the balance typed when the card was added):
 * those purchases were never counted as spending, so paying it is real cash leaving. Its payments
 * (up to the opening balance) and its unpaid opening rest are commitments; the rest of that
 * statement and every later statement stay transfers (decision 06). The payments are transfers,
 * never spending, so nothing counts twice.
 */
function openingItems(d: Due, today: LocalDate): CommitmentItem[] {
  const opening = d.card?.openingMinor ?? 0;
  if (opening <= 0 || d.status === 'closed' || d.status === 'skipped') return [];
  const label = d.payeeName + ' (before Tahi)';
  const items: CommitmentItem[] = [];
  let left = opening;
  for (const p of d.payments) {
    const amountMinor = Math.min(p.amountMinor, left);
    if (amountMinor <= 0) break;
    left -= amountMinor;
    items.push({
      id: p.id,
      label,
      date: expenseDateFor(p.paidDate, d.dueDate),
      amountMinor,
      estimated: false,
      paid: true,
      ledgerEntryId: p.ledgerEntryId,
    });
  }
  const owing = d.status === 'open' || d.status === 'partial' || d.status === 'rolled';
  if (owing && left > 0) items.push(owedItem(d, left, label, today));
  return items;
}

/**
 * Budget's cash-flow commitments (spec "Income and the monthly picture"): fixed bills, utilities,
 * loans and card installments in the range, plus a card's balance from before Tahi. Other card
 * statements are never a commitment: their purchases already counted when bought (decision 06),
 * and paying one is a transfer. Unpaid dues whose date has passed are held back in the period
 * that holds today.
 */
export function paymentCommitments(
  snap: PaymentsSnapshot,
  range: DateRange,
  today: LocalDate,
): CommitmentLine[] {
  const lines: Record<PaymentLineKey, CommitmentItem[]> = {
    bills: [],
    utilities: [],
    loans: [],
    installments: [],
    cards: [],
    card_statements: [],
  };
  const payees = new Map(snap.payees.filter((p) => p.deletedAt === null).map((p) => [p.id, p]));
  for (const d of allDues(snap, range.end > today ? range.end : addDays(today, 1), today)) {
    const key = lineOf(d.payeeKind);
    const items = key ? dueItems(d, today) : d.card ? openingItems(d, today) : [];
    lines[key ?? 'cards'].push(...items.filter((i) => inRange(i.date, range)));
    // v0.5 cash view: a card statement's unpaid rest is cash that will leave an account.
    if (d.card && isOpen(d) && d.remainingMinor > 0) {
      const owed = owedItem(d, d.remainingMinor, `${d.payeeName} statement`, today);
      if (inRange(owed.date, range)) lines.card_statements.push(owed);
    }
  }
  // Flexible-loan payments made at any time (no due).
  for (const p of snap.payments) {
    if (p.deletedAt !== null || p.dueId !== null) continue;
    const payee = payees.get(p.payeeId);
    if (!payee || payee.kind !== 'loan' || !inRange(p.paidDate, range)) continue;
    lines.loans.push({
      id: p.id,
      label: payee.name,
      date: p.paidDate,
      amountMinor: p.amountMinor,
      estimated: false,
      paid: true,
      ledgerEntryId: p.ledgerEntryId,
    });
  }
  for (const i of snap.installments) {
    if (i.deletedAt !== null) continue;
    const card = payees.get(i.cardId);
    if (!card) continue;
    for (const c of recordedCharges(snap, i, card)) {
      if (!c.tracked || !inRange(c.date, range)) continue;
      lines.installments.push({
        id: installmentEntryId(snap.userId, i.id, c.month),
        label: `${i.item} (${c.n}/${i.months})`,
        date: c.date,
        amountMinor: c.amountMinor,
        estimated: false,
        paid: c.date <= today,
        ledgerEntryId: installmentEntryId(snap.userId, i.id, c.month),
      });
    }
  }
  return (Object.keys(lines) as PaymentLineKey[])
    .filter((k) => lines[k].length > 0)
    .map((k) => ({
      key: k,
      label: LINE_LABELS[k],
      ...(k === 'card_statements'
        ? { basis: 'cash' as const }
        : k === 'cards' || k === 'installments'
          ? { basis: 'cashflow' as const }
          : {}),
      items: lines[k].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)),
    }));
}

/**
 * After an installment's amount, months, start month, item or category changes: its charges
 * already written follow the new terms (amount, "n/months" note, date, category), and a month the
 * installment no longer charges is soft-deleted. Months whose card statement is already paid keep
 * what was charged. Months newly due are written by the catch-up. `snap` is from before the edit.
 */
export function buildInstallmentChargeFixes(
  snap: PaymentsSnapshot,
  edited: InstallmentLike,
  today: LocalDate,
  ctx: BuildCtx,
): Mutation[] {
  const card = snap.payees.find(
    (p) => p.id === edited.cardId && p.kind === 'card' && p.deletedAt === null,
  );
  if (!card) return [];
  const prefix = installmentEntryId(snap.userId, edited.id, '');
  const rows = snap.cardEntries
    .filter((e) => e.deletedAt === null && e.sourceModule === 'payments' && e.id.startsWith(prefix))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  if (rows.length === 0) return [];
  const statements = new Map(payeeDues(snap, card, today, today).map((d) => [d.dueDate, d]));
  const charges = new Map(
    installmentCharges(edited, card)
      .filter((c) => c.tracked && c.date <= today)
      .map((c) => [c.month, c]),
  );
  const out: Mutation[] = [];
  for (const row of rows) {
    if (statements.get(row.date)?.status === 'paid') continue;
    const c = charges.get(row.id.slice(prefix.length));
    if (!c) {
      out.push(paymentsMutation(ctx, 'ledger_entries', 'delete', row.id));
      continue;
    }
    const next: Record<string, unknown> = {
      date: c.date,
      amountMinor: c.amountMinor,
      categoryId: edited.categoryId,
      note: edited.item + ' (' + c.n + '/' + edited.months + ')',
    };
    const before: Record<string, unknown> = {
      date: row.date,
      amountMinor: row.amountMinor,
      categoryId: row.categoryId,
      note: row.note,
    };
    const changed = Object.fromEntries(Object.entries(next).filter(([k, v]) => v !== before[k]));
    if (Object.keys(changed).length > 0)
      out.push(paymentsMutation(ctx, 'ledger_entries', 'update', row.id, changed));
  }
  return out;
}

/**
 * Installment months whose charge date has come and that have no ledger row yet (live or deleted,
 * so a charge the user deleted in Spending stays deleted). Months whose charge date passed while
 * Spending was off are written once it's usable again (OPEN-QUESTIONS v0.5 Q5).
 */
export function pendingInstallmentCharges(
  snap: PaymentsSnapshot,
  existingEntryIds: ReadonlySet<string>,
  today: LocalDate,
): { installment: InstallmentLike; card: PayeeLike; charge: InstallmentCharge; entryId: string }[] {
  const cards = new Map(
    snap.payees.filter((p) => p.deletedAt === null && p.kind === 'card').map((p) => [p.id, p]),
  );
  return snap.installments.flatMap((installment) => {
    const card = cards.get(installment.cardId);
    if (!card || installment.deletedAt !== null) return [];
    return dueInstallmentCharges(installment, card, today)
      .map((charge) => ({
        installment,
        card,
        charge,
        entryId: installmentEntryId(snap.userId, installment.id, charge.month),
      }))
      .filter((c) => !existingEntryIds.has(c.entryId));
  });
}

/**
 * Each charge is one card expense (decision 06: installments count month by month): method
 * card:<id> on the charge date, the installment's category, no fund (a Payments expense never
 * takes Budget's default fund: it's already a commitment). Deterministic ids and
 * insert-if-absent, so two devices catching up write one row.
 */
export function buildInstallmentCharges(
  pending: ReturnType<typeof pendingInstallmentCharges>,
  ctx: BuildCtx,
): Mutation[] {
  return pending.map(({ installment: i, card, charge, entryId }) =>
    paymentsMutation(
      ctx,
      'ledger_entries',
      'insert',
      entryId,
      {
        date: charge.date,
        amountMinor: charge.amountMinor,
        currency: 'PHP',
        kind: 'expense',
        categoryId: i.categoryId,
        method: cardMethod(card.id),
        fundId: null,
        sourceModule: 'payments',
        sourceId: i.id,
        note: `${i.item} (${charge.n}/${i.months})`,
        recordedTz: ctx.tz,
      },
      { ifAbsent: true },
    ),
  );
}
